import { useEffect, useMemo, useState } from "react";
import { getGalleryCaptions } from "@/lib/villaApi";
import type { GalleryImage } from "@/components/feature/GalleryGrid";
import { useCurrentLang } from "@/i18n/language";
import { galleryKeyForLang } from "@/lib/galleries";

type CaptionRow = { image_key: string; caption: string };

const toMap = (rows: CaptionRow[] | null): Record<string, string> => {
  const map: Record<string, string> = {};
  (rows || []).forEach((row) => {
    if (
      row &&
      typeof row.image_key === "string" &&
      typeof row.caption === "string" &&
      row.caption.trim().length > 0
    ) {
      map[row.image_key] = row.caption;
    }
  });
  return map;
};

/**
 * Lädt die (im Backstage gepflegten) Bild-Beschriftungen einer Galerie
 * aus der Datenbank – getrennt nach Sprache (Deutsch / Englisch) – und legt
 * sie über die Standard-Beschriftungen aus dem Code.
 *
 * Fallback-Kette (Englisch): gespeicherte EN -> gespeicherte DE -> EN-Standard
 * aus dem Code -> DE-Standard. So ist die Galerie nie leer und Englisch wird
 * nur dann deutsch, wenn für ein Bild gar kein englischer Text existiert.
 */
export function useGalleryCaptions(
  galleryKey: string,
  defaults: GalleryImage[]
): GalleryImage[] {
  const lang = useCurrentLang();
  const [deMap, setDeMap] = useState<Record<string, string>>({});
  const [enMap, setEnMap] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [deRes, enRes] = await Promise.all([
          getGalleryCaptions(galleryKeyForLang(galleryKey, "de")),
          getGalleryCaptions(galleryKeyForLang(galleryKey, "en")),
        ]);
        if (!active) return;
        setDeMap(toMap(deRes.data));
        setEnMap(toMap(enRes.data));
      } catch {
        // Bei Fehlern bleiben die Standard-Beschriftungen aktiv.
        if (active) {
          setDeMap({});
          setEnMap({});
        }
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [galleryKey]);

  return useMemo(
    () =>
      defaults.map((image) => {
        const caption =
          lang === "en"
            ? enMap[image.key] ?? deMap[image.key] ?? image.captionEn ?? image.caption
            : deMap[image.key] ?? image.caption;
        return { ...image, caption };
      }),
    [defaults, deMap, enMap, lang]
  );
}